// ============================================
// Protocol PDF Export
//
// Generates a branded, HIPAA-safe PDF from protocol data
// using a hidden iframe + browser print dialog (window.print).
// No external PDF library required.
//
// Design system: DM Sans body, Instrument Serif titles,
// JetBrains Mono lab values, Rose #E11D48 accent,
// 13px min font, WCAG AA contrast, mobile-friendly.
// ============================================

import type { Protocol, SupplementRecommendation, ProtocolTiming, BiomarkerResult } from '../types';

interface PatientContext {
  age: number;
  sex: 'female' | 'male';
  biomarkers?: BiomarkerResult[];
}

// ── Design tokens (inline for iframe isolation) ──
// All text colors verified against WCAG AA (4.5:1 minimum on their backgrounds)

const C = {
  accent: '#E11D48',       // 4.6:1 on white — AA pass
  accentDark: '#BE123C',   // 5.6:1 on white — AA pass (used for small text)
  accentSoft: '#FFF1F2',
  text: '#18181B',         // 16.5:1 on white — AAA pass
  textSoft: '#3F3F46',     // 9.5:1 on white — AAA pass
  textMuted: '#52525B',    // 7.2:1 on white — AAA pass
  textCaption: '#71717A',  // 4.6:1 on white — AA pass (smallest allowed)
  bg: '#FFFFFF',
  bgSoft: '#FAFAFA',
  border: '#E4E4E7',
  borderSoft: '#F4F4F5',
  // Status colors darkened for WCAG AA on white/soft backgrounds
  success: '#15803D',      // 5.8:1 on white — AA pass
  successSoft: '#DCFCE7',
  successText: '#166534',  // 7.1:1 on #DCFCE7 — AAA pass
  warning: '#92400E',      // 6.4:1 on white — AA pass
  warningSoft: '#FEF3C7',
  warningText: '#78350F',  // 7.5:1 on #FEF3C7 — AAA pass
  danger: '#B91C1C',       // 5.7:1 on white — AA pass
  dangerSoft: '#FEF2F2',
  dangerBorder: '#FECACA',
  dangerText: '#7F1D1D',   // 9.4:1 on #FEF2F2 — AAA pass
} as const;

const TIMING_LABELS: Record<ProtocolTiming, { label: string; icon: string; time: string }> = {
  AM_ENERGY: { label: 'Morning', icon: '☀️', time: 'Take with breakfast' },
  MIDDAY_ABSORPTION: { label: 'Midday', icon: '☕', time: 'Take between meals' },
  PM_RECOVERY: { label: 'Evening', icon: '🌙', time: 'Take before bed' },
};

const EVIDENCE_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  strong: { bg: C.successSoft, text: C.successText, label: 'Strong Evidence' },
  moderate: { bg: C.warningSoft, text: C.warningText, label: 'Moderate Evidence' },
  emerging: { bg: '#F3F4F6', text: '#4B5563', label: 'Emerging' },
};

const STATUS_STYLE: Record<string, { color: string; bg: string; label: string; dot: string }> = {
  optimal: { color: C.successText, bg: C.successSoft, label: 'Optimal', dot: C.success },
  suboptimal: { color: C.warningText, bg: C.warningSoft, label: 'Needs Work', dot: '#D97706' },
  low: { color: C.dangerText, bg: C.dangerSoft, label: 'Low', dot: C.danger },
  elevated: { color: C.dangerText, bg: C.dangerSoft, label: 'Elevated', dot: C.danger },
  critical: { color: C.dangerText, bg: C.dangerSoft, label: 'Critical', dot: C.danger },
};

// ── Helpers ──

function formatDate(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Returns a 0–100 percentage for where a value falls within a range, clamped. */
function rangePercent(value: number, low: number, high: number): number {
  if (high <= low) return 50;
  const pct = ((value - low) / (high - low)) * 100;
  return Math.max(0, Math.min(100, pct));
}

// ── Biomarker range bar (visual where-you-fall indicator) ──

function buildRangeBar(b: BiomarkerResult): string {
  const st = STATUS_STYLE[b.hemologixStatus] || STATUS_STYLE.suboptimal;
  // Use a wider range for the bar (80% below low, 120% above high)
  const barLow = b.functionalRange.low * 0.6;
  const barHigh = b.functionalRange.high * 1.4;
  const valuePct = rangePercent(b.value, barLow, barHigh);
  const rangeLowPct = rangePercent(b.functionalRange.low, barLow, barHigh);
  const rangeHighPct = rangePercent(b.functionalRange.high, barLow, barHigh);

  return `
    <div style="position:relative;height:8px;background:#E4E4E7;border-radius:4px;overflow:visible;margin:6px 0 2px;">
      <div style="position:absolute;left:${rangeLowPct}%;right:${100 - rangeHighPct}%;top:0;bottom:0;background:${C.successSoft};border-radius:4px;border:1px solid ${C.success};opacity:0.5;"></div>
      <div style="position:absolute;left:${Math.max(0, valuePct - 1)}%;top:-2px;width:4px;height:12px;background:${st.dot};border-radius:2px;"></div>
    </div>`;
}

// ── Supplement card ──

function buildSupplementCard(supp: SupplementRecommendation): string {
  const ev = EVIDENCE_STYLE[supp.evidenceLevel] || EVIDENCE_STYLE.moderate;

  const warnings = supp.interactionWarnings
    .map((w) => `<div style="color:${C.warningText};font-size:12px;margin-top:6px;line-height:1.45;">⚠ ${esc(w)}</div>`)
    .join('');

  const rxFlag = supp.requiresRx
    ? `<div style="color:${C.accentDark};font-size:12px;font-weight:600;margin-top:6px;">🩺 Rx — Discuss with your provider</div>`
    : '';

  return `
    <div style="padding:16px 18px;border:1px solid ${C.border};border-left:3px solid ${C.accent};border-radius:4px;margin-bottom:10px;background:${C.bg};page-break-inside:avoid;">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:6px;">
        <div style="font-weight:600;font-size:15px;color:${C.text};">${esc(supp.name)}</div>
        <span style="flex-shrink:0;display:inline-block;padding:2px 10px;border-radius:10px;font-size:11px;font-weight:600;background:${ev.bg};color:${ev.text};white-space:nowrap;">${ev.label}</span>
      </div>
      <div style="font-family:'JetBrains Mono',monospace;font-size:14px;color:${C.accentDark};font-weight:500;margin-bottom:8px;">${esc(supp.dosage)}</div>
      <div style="font-size:13px;color:${C.textSoft};line-height:1.55;">${esc(supp.rationale)}</div>
      ${warnings}${rxFlag}
    </div>`;
}

// ── Timing section ──

function buildTimingSection(timing: ProtocolTiming, supplements: SupplementRecommendation[]): string {
  if (supplements.length === 0) return '';
  const cfg = TIMING_LABELS[timing];

  return `
    <div style="margin-bottom:32px;page-break-inside:avoid;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px;padding-bottom:10px;border-bottom:2px solid ${C.accent};">
        <span style="font-size:20px;line-height:1;">${cfg.icon}</span>
        <span style="font-family:'Instrument Serif',Georgia,serif;font-size:20px;font-weight:400;color:${C.text};">${cfg.label}</span>
        <span style="font-size:13px;color:${C.textCaption};margin-left:4px;">— ${cfg.time}</span>
      </div>
      ${supplements.map(buildSupplementCard).join('')}
    </div>`;
}

// ── Biomarker comparison section ──

function buildBiomarkerSection(biomarkers: BiomarkerResult[]): string {
  if (biomarkers.length === 0) return '';

  type Group = { key: string; label: string; color: string; dotColor: string; bg: string; textColor: string; items: BiomarkerResult[] };
  const groups: Group[] = [
    { key: 'red', label: 'Needs Attention', color: C.danger, dotColor: C.danger, bg: C.dangerSoft, textColor: C.dangerText, items: [] },
    { key: 'yellow', label: 'Room to Improve', color: '#D97706', dotColor: '#D97706', bg: C.warningSoft, textColor: C.warningText, items: [] },
    { key: 'green', label: 'Looking Good', color: C.success, dotColor: C.success, bg: C.successSoft, textColor: C.successText, items: [] },
  ];

  for (const b of biomarkers) {
    if (b.hemologixStatus === 'optimal') groups[2].items.push(b);
    else if (b.hemologixStatus === 'suboptimal') groups[1].items.push(b);
    else groups[0].items.push(b);
  }

  const suboptimalCount = biomarkers.filter((b) => b.hemologixStatus !== 'optimal').length;

  const groupHtml = groups
    .filter((g) => g.items.length > 0)
    .map((g) => {
      const rows = g.items
        .map((b) => {
          const st = STATUS_STYLE[b.hemologixStatus] || STATUS_STYLE.suboptimal;
          return `
          <tr>
            <td style="padding:10px 12px;border-bottom:1px solid ${C.borderSoft};font-weight:500;font-size:13px;color:${C.text};width:25%;">${esc(b.biomarker)}</td>
            <td style="padding:10px 12px;border-bottom:1px solid ${C.borderSoft};text-align:right;font-family:'JetBrains Mono',monospace;font-size:13px;color:${C.text};width:15%;">
              ${b.value} <span style="font-size:11px;color:${C.textCaption};font-family:'DM Sans',sans-serif;">${esc(b.unit)}</span>
            </td>
            <td style="padding:10px 12px;border-bottom:1px solid ${C.borderSoft};text-align:center;font-size:12px;color:${b.standardStatus === 'normal' ? C.textMuted : C.danger};width:13%;">${b.standardStatus === 'normal' ? '✓ Normal' : '✗ Flag'}</td>
            <td style="padding:10px 12px;border-bottom:1px solid ${C.borderSoft};text-align:center;width:15%;">
              <span style="display:inline-block;padding:2px 10px;border-radius:10px;font-size:11px;font-weight:600;background:${st.bg};color:${st.color};">${st.label}</span>
            </td>
            <td style="padding:10px 12px;border-bottom:1px solid ${C.borderSoft};font-size:11px;color:${C.textMuted};font-family:'JetBrains Mono',monospace;text-align:center;width:14%;">${b.functionalRange.low}–${b.functionalRange.high}</td>
            <td class="bio-range-col" style="padding:10px 12px;border-bottom:1px solid ${C.borderSoft};width:18%;">
              ${buildRangeBar(b)}
            </td>
          </tr>`;
        })
        .join('');

      return `
        <tr>
          <td colspan="6" style="padding:0;">
            <div style="display:flex;align-items:center;gap:8px;padding:10px 12px;background:${g.bg};">
              <span style="display:inline-block;width:10px;height:10px;border-radius:5px;background:${g.dotColor};flex-shrink:0;"></span>
              <span style="font-weight:600;font-size:13px;color:${g.textColor};">${g.label}</span>
              <span style="margin-left:auto;font-size:12px;color:${C.textMuted};">${g.items.length} marker${g.items.length !== 1 ? 's' : ''}</span>
            </div>
          </td>
        </tr>
        ${rows}`;
    })
    .join('');

  // Mobile stacked cards for biomarkers
  const mobileCards = groups
    .filter((g) => g.items.length > 0)
    .map((g) => {
      const cards = g.items.map((b) => {
        const st = STATUS_STYLE[b.hemologixStatus] || STATUS_STYLE.suboptimal;
        return `
        <div style="padding:12px;border:1px solid ${C.border};border-radius:4px;margin-bottom:8px;background:${C.bg};">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <span style="font-weight:600;font-size:13px;color:${C.text};">${esc(b.biomarker)}</span>
            <span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;background:${st.bg};color:${st.color};">${st.label}</span>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px;">
            <span style="font-family:'JetBrains Mono',monospace;font-size:14px;font-weight:500;color:${C.text};">${b.value} <span style="font-size:11px;color:${C.textCaption};font-family:'DM Sans',sans-serif;">${esc(b.unit)}</span></span>
            <span style="font-size:11px;color:${C.textMuted};">Optimal: <span style="font-family:'JetBrains Mono',monospace;">${b.functionalRange.low}–${b.functionalRange.high}</span></span>
          </div>
          ${buildRangeBar(b)}
          <div style="display:flex;justify-content:space-between;margin-top:6px;">
            <span style="font-size:11px;color:${b.standardStatus === 'normal' ? C.textMuted : C.danger};">Doctor: ${b.standardStatus === 'normal' ? '✓ Normal' : '✗ Flagged'}</span>
          </div>
        </div>`;
      }).join('');

      return `
      <div style="margin-bottom:16px;">
        <div style="display:flex;align-items:center;gap:8px;padding:8px 0;margin-bottom:4px;">
          <span style="display:inline-block;width:10px;height:10px;border-radius:5px;background:${g.dotColor};flex-shrink:0;"></span>
          <span style="font-weight:600;font-size:13px;color:${g.textColor};">${g.label}</span>
          <span style="font-size:12px;color:${C.textMuted};">(${g.items.length})</span>
        </div>
        ${cards}
      </div>`;
    }).join('');

  return `
    <div style="margin-bottom:32px;page-break-before:auto;">
      <div style="font-family:'Instrument Serif',Georgia,serif;font-size:22px;color:${C.text};margin-bottom:6px;">Your Lab Comparison</div>
      <div style="font-size:13px;color:${C.textMuted};margin-bottom:16px;">${biomarkers.length} biomarkers analyzed · ${suboptimalCount} outside optimal range</div>

      <!-- Desktop table -->
      <div class="bio-table-desktop" style="border:1px solid ${C.border};border-radius:8px;overflow:hidden;">
        <table style="width:100%;border-collapse:collapse;" cellpadding="0" cellspacing="0">
          <thead>
            <tr style="background:${C.text};">
              <th style="text-align:left;padding:10px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:${C.bg};font-weight:600;">Marker</th>
              <th style="text-align:right;padding:10px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:${C.bg};font-weight:600;">Value</th>
              <th style="text-align:center;padding:10px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:${C.bg};font-weight:600;">Doctor</th>
              <th style="text-align:center;padding:10px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:${C.bg};font-weight:600;">HemoLogix</th>
              <th style="text-align:center;padding:10px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:${C.bg};font-weight:600;">Optimal</th>
              <th class="bio-range-col" style="text-align:center;padding:10px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:${C.bg};font-weight:600;">Range</th>
            </tr>
          </thead>
          <tbody>
            ${groupHtml}
          </tbody>
        </table>
      </div>

      <!-- Mobile stacked cards -->
      <div class="bio-cards-mobile" style="display:none;">
        ${mobileCards}
      </div>

      <div style="font-size:11px;color:${C.textCaption};margin-top:8px;line-height:1.5;">Compared against functional medicine optimal ranges, not general population references. The green zone on each range bar shows the optimal range.</div>
    </div>`;
}

// ── Safety warnings ──

function buildSafetyWarnings(protocol: Protocol): string {
  if (!protocol.safetyWarnings || protocol.safetyWarnings.length === 0) return '';

  return `
    <div style="margin-bottom:24px;padding:16px;border:1px solid ${C.dangerBorder};border-radius:8px;background:${C.dangerSoft};page-break-inside:avoid;">
      <div style="font-weight:700;color:${C.danger};font-size:14px;margin-bottom:8px;">⚠ Safety Alerts</div>
      ${protocol.safetyWarnings.map((w) => `<div style="color:${C.dangerText};font-size:13px;margin-bottom:4px;line-height:1.5;">• ${esc(w)}</div>`).join('')}
    </div>`;
}

// ── Main HTML builder ──

function buildPdfHtml(protocol: Protocol, patient: PatientContext): string {
  const timings: ProtocolTiming[] = ['AM_ENERGY', 'MIDDAY_ABSORPTION', 'PM_RECOVERY'];

  const timingSections = timings
    .map((t) => {
      const supplements = protocol.supplements.filter((s) => s.timing === t);
      return buildTimingSection(t, supplements);
    })
    .join('');

  const biomarkerSection = patient.biomarkers && patient.biomarkers.length > 0
    ? buildBiomarkerSection(patient.biomarkers)
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>HemoLogix Protocol</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Instrument+Serif&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    html { font-size: 15px; }

    body {
      font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 15px;
      color: ${C.text};
      background: ${C.bg};
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    .page {
      max-width: 720px;
      margin: 0 auto;
      padding: 40px 32px;
    }

    @page {
      size: letter;
      margin: 0.6in 0.7in;
    }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 0; max-width: none; }
      .bio-cards-mobile { display: none !important; }
      .bio-table-desktop { display: block !important; }
      .bio-range-col { display: table-cell !important; }
    }

    /* Mobile: tighter padding, stacked biomarker cards */
    @media screen and (max-width: 600px) {
      html { font-size: 14px; }
      .page { padding: 20px 16px; }
      .header-flex { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
      .header-right { text-align: left !important; }
      .bio-table-desktop { display: none !important; }
      .bio-cards-mobile { display: block !important; }
    }

    /* Tablets and smaller desktops: hide range bar column */
    @media screen and (max-width: 780px) and (min-width: 601px) {
      .bio-range-col { display: none !important; }
    }
  </style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="header-flex" style="display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:16px;margin-bottom:28px;border-bottom:3px solid ${C.accent};">
    <div>
      <div style="font-family:'Instrument Serif',Georgia,serif;font-size:32px;color:${C.accent};letter-spacing:-0.5px;line-height:1.1;">HemoLogix</div>
      <div style="font-size:13px;color:${C.textMuted};margin-top:4px;letter-spacing:0.02em;">Personalized Supplement Protocol</div>
    </div>
    <div class="header-right" style="text-align:right;font-size:13px;color:${C.textMuted};">
      <div>${formatDate()}</div>
      <div style="margin-top:2px;">${patient.age}-year-old ${patient.sex}</div>
    </div>
  </div>

  <!-- Summary -->
  <div style="margin-bottom:28px;padding:18px 20px;border-radius:8px;background:${C.bgSoft};border:1px solid ${C.border};">
    <div style="font-family:'Instrument Serif',Georgia,serif;font-size:18px;color:${C.text};margin-bottom:8px;">Protocol Summary</div>
    <div style="font-size:14px;color:${C.textSoft};line-height:1.65;">${esc(protocol.summary)}</div>
    ${protocol.bundleName ? `<div style="margin-top:12px;padding-top:12px;border-top:1px solid ${C.border};display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
      <span style="font-weight:600;font-size:14px;color:${C.text};">📦 ${esc(protocol.bundleName)}</span>
      ${protocol.bundlePrice ? `<span style="font-family:'JetBrains Mono',monospace;font-size:14px;color:${C.accentDark};font-weight:600;">${esc(protocol.bundlePrice)}</span>` : ''}
      <span style="font-size:13px;color:${C.textMuted};">· ${protocol.supplements.length} supplement${protocol.supplements.length !== 1 ? 's' : ''}</span>
    </div>` : ''}
  </div>

  <!-- Safety Warnings -->
  ${buildSafetyWarnings(protocol)}

  <!-- Biomarker Comparison -->
  ${biomarkerSection}

  <!-- Section divider before supplements -->
  ${biomarkerSection ? `<div style="font-family:'Instrument Serif',Georgia,serif;font-size:22px;color:${C.text};margin-bottom:20px;">Your Supplement Protocol</div>` : ''}

  <!-- Supplement Sections by Timing -->
  ${timingSections}

  <!-- DSHEA Disclaimers -->
  <div style="margin-top:36px;padding:18px 20px;border-radius:8px;background:${C.bgSoft};border:1px solid ${C.border};page-break-inside:avoid;">
    <div style="font-weight:700;font-size:11px;color:${C.textMuted};margin-bottom:10px;text-transform:uppercase;letter-spacing:0.08em;">Important Disclaimers</div>
    <div style="font-size:13px;color:${C.textCaption};line-height:1.65;">
      <p style="margin-bottom:8px;font-weight:700;font-size:14px;color:${C.text};">Not for diagnostic use.</p>
      <p style="margin-bottom:8px;font-weight:600;color:${C.textMuted};">HemoLogix provides wellness analysis, not medical diagnosis. This report should not be used as a substitute for professional medical evaluation. Consult your healthcare provider before starting any supplement regimen. HemoLogix does not diagnose, treat, cure, or prevent any disease.</p>
      <p style="margin-bottom:8px;">These statements have not been evaluated by the Food and Drug Administration. This protocol is not intended to diagnose, treat, cure, or prevent any disease.</p>
      <p style="margin-bottom:8px;">Protocol designed under the supervision of Dr. Christopher Calapai, D.O.</p>
      ${protocol.warnings.map((w) => `<p style="margin-bottom:4px;">${esc(w)}</p>`).join('')}
    </div>
  </div>

  <!-- Footer -->
  <div style="margin-top:24px;padding-top:14px;border-top:1px solid ${C.border};">
    <div style="text-align:center;font-size:13px;font-weight:600;color:${C.textMuted};margin-bottom:10px;">Not for diagnostic use.</div>
    <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
      <div style="font-family:'Instrument Serif',Georgia,serif;font-size:15px;color:${C.accent};">HemoLogix</div>
      <div style="font-size:11px;color:${C.textCaption};">hemologix.ai · Confidential patient document</div>
    </div>
  </div>

</div>
</body>
</html>`;
}

/**
 * Generate and download a branded PDF of the patient's protocol.
 * Uses a hidden iframe + window.print for zero-dependency PDF generation.
 *
 * HIPAA note: No patient name or email is included in the PDF.
 * Only age and sex are shown for clinical context.
 */
export function exportProtocolPdf(
  protocol: Protocol,
  patient: PatientContext,
): void {
  const html = buildPdfHtml(protocol, patient);

  // Create a hidden iframe to render and print
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.opacity = '0';
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDoc) {
    console.error('[PDF Export] Failed to access iframe document');
    document.body.removeChild(iframe);
    return;
  }

  iframeDoc.open();
  iframeDoc.write(html);
  iframeDoc.close();

  // Wait for fonts to load before printing to ensure correct rendering
  iframe.onload = () => {
    const win = iframe.contentWindow;
    if (!win) return;

    const triggerPrint = () => {
      try {
        win.print();
      } catch (err) {
        console.error('[PDF Export] Print failed:', err);
      }
      setTimeout(() => {
        if (iframe.parentElement) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    };

    // Use document.fonts.ready if available for reliable font loading
    if (win.document.fonts && typeof win.document.fonts.ready === 'object') {
      win.document.fonts.ready.then(() => {
        triggerPrint();
      }).catch(() => {
        // Fallback: print anyway after delay
        setTimeout(triggerPrint, 800);
      });
    } else {
      setTimeout(triggerPrint, 800);
    }
  };

  // Fallback: if onload doesn't fire (some browsers), trigger after 3s
  setTimeout(() => {
    if (iframe.parentElement) {
      try {
        iframe.contentWindow?.print();
      } catch {
        // Already cleaned up or print not supported
      }
      setTimeout(() => {
        if (iframe.parentElement) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    }
  }, 4000);
}
